import { FREE_GIFT_CLASSES, MAX_QTY } from '@/components/blocks/FreeGiftV2/constants';
import { useFreeGiftPromotion } from '@/components/blocks/FreeGiftV2/context/FreeGiftPromotionContext';
import { ProductDetailsSx } from '@/components/blocks/FreeGiftV2/styles/freeGift';
import {
	generateImageUrl,
	getProductDetailsByPartNumber,
} from '@/components/blocks/FreeGiftV2/utils';
import { FormatPrice } from '@/components/blocks/HDMPriceDisplay/FormatPrice';
import { Linkable } from '@/components/blocks/Linkable';
import { Img } from '@/components/blocks/MaterialImage';
import { NumberInput } from '@/components/blocks/NumberInput';
import { useOnTrackSelectItem } from '@/components/blocks/PLPProductCard/hooks/useOnTrackSelectItem';
import { useLocalization } from '@/data/Localization';
import { getHref_Product } from '@/utils/getHref_Product';
import { parseHTML } from '@/utils/parseHTMLModern';
import { Box, Chip, Stack, Typography } from '@mui/material';
import { GIFT_TYPE, GiftType } from 'presentation/events/constants/eventConstants';
import { OpenSansFontWeights } from 'shared/custom/constants/commonConstants';

interface Props {
	sku: string;
	quantity?: number;
	isParentProduct?: boolean;
	isPurchaseView?: boolean;
	selected?: boolean;
	combinable?: boolean;
	maxQuantity?: number;
	onQuantityChange?: (quantity: number) => void;
	outOfStock?: boolean;
}

const getDrawerGiftType = (
	promoThdCode: string | undefined,
	isPurchaseView: boolean
): GiftType | undefined => {
	if (!promoThdCode) return undefined;
	return isPurchaseView ? GIFT_TYPE.TRIGGER : GIFT_TYPE.GIFT;
};

const ParentProductChip = ({ show, label }: { show: boolean; label: string }) =>
	show ? <Chip label={label} size="small" sx={ProductDetailsSx.parentChip} /> : null;

const ProductNameLink = ({
	href,
	manufacturer,
	name,
	onClick,
}: {
	href: ReturnType<typeof getHref_Product> | undefined;
	manufacturer: string;
	name: string;
	onClick: () => void;
}) => (
	<Linkable
		href={href}
		target="_blank"
		rel="noopener noreferrer"
		color="inherit"
		underline="none"
		onClick={onClick}
	>
		<Typography
			variant="bodySubText"
			color="grey.500"
			className={FREE_GIFT_CLASSES.productName}
			textTransform="capitalize"
		>
			<Typography
				component="span"
				variant="bodySubText"
				sx={{ fontWeight: OpenSansFontWeights.semibold }}
				className={FREE_GIFT_CLASSES.productBrand}
				textTransform="capitalize"
			>
				{manufacturer && parseHTML(manufacturer)}{' '}
			</Typography>
			{name && parseHTML(name)}
		</Typography>
	</Linkable>
);

const ProductQuantityLabel = ({
	show,
	quantity,
	label,
}: {
	show: boolean;
	quantity?: number;
	label: string;
}) =>
	show ? (
		<Typography
			variant="bodyCaption"
			color="grey.300"
			className={FREE_GIFT_CLASSES.productQuantity}
		>
			{label}: {quantity}
		</Typography>
	) : null;

const ProductPriceSection = ({
	isPurchaseView,
	price,
	freeLabel,
}: {
	isPurchaseView: boolean;
	price: number;
	freeLabel: string;
}) => (
	<Box display="flex" gap="5px">
		<Typography
			variant="bodySubText"
			color="grey.500"
			fontWeight={OpenSansFontWeights.semibold}
			sx={isPurchaseView ? null : ProductDetailsSx.priceText}
			className={FREE_GIFT_CLASSES.productPrice}
		>
			<FormatPrice price={price} type="simple" />
		</Typography>
		{isPurchaseView ? null : (
			<Typography
				variant="bodySubText"
				color="success.main"
				fontWeight={OpenSansFontWeights.semibold}
			>
				{freeLabel}
			</Typography>
		)}
	</Box>
);

const OutOfStockLabel = ({ show, label }: { show: boolean; label: string }) =>
	show ? (
		<Typography variant="bodySubText" color="grey.300" fontWeight={OpenSansFontWeights.semibold}>
			{label}
		</Typography>
	) : null;

const ProductQuantityInput = ({
	show,
	quantity,
	isParentProduct,
	maxQuantity,
	onQuantityChange,
}: {
	show: boolean;
	quantity?: number;
	isParentProduct: boolean;
	maxQuantity: number;
	onQuantityChange?: (quantity: number) => void;
}) =>
	show ? (
		<NumberInput
			onChange={(qty) => onQuantityChange?.(qty ?? 0)}
			value={quantity ?? 0}
			min={isParentProduct ? 1 : 0}
			max={maxQuantity}
			showControls
			disallowEmptyOnBlur
			fullWidth
			className={FREE_GIFT_CLASSES.productQuantity}
		/>
	) : null;

export const ProductDetails = (props: Props) => {
	const {
		sku,
		quantity,
		isParentProduct = false,
		isPurchaseView = false,
		selected = false,
		combinable = false,
		maxQuantity = MAX_QTY,
		onQuantityChange,
		outOfStock = false,
	} = props;
	const labels = useLocalization('FreeGift');
	const { SKU, Quantity, Availability } = useLocalization('HDMProductDetail');
	const { promoItems, promoThdCode } = useFreeGiftPromotion();
	const product = getProductDetailsByPartNumber(promoItems, sku);
	const onTrackSelectItem = useOnTrackSelectItem({
		product,
		promoThdCode,
		giftType: getDrawerGiftType(promoThdCode, isPurchaseView),
	});
	const name = product?.name.toLowerCase() ?? '';
	const manufacturer = product?.manufacturer?.toLowerCase() ?? '';
	const productUrl = generateImageUrl(sku);
	const price = product?.productPrice.offer ?? 0;
	const productHref = product && getHref_Product(product, undefined);

	return (
		<Stack
			display="flex"
			gap="10px"
			className={selected ? FREE_GIFT_CLASSES.promotionSelected : ''}
			sx={outOfStock ? ProductDetailsSx.outOfStock : undefined}
		>
			<ParentProductChip show={isParentProduct && !combinable} label={labels.ParentProduct.t()} />
			<Box sx={ProductDetailsSx.container}>
				<Box sx={ProductDetailsSx.imageContainer}>
					<Img
						sx={ProductDetailsSx.image}
						src={productUrl}
						alt={sku}
						className={FREE_GIFT_CLASSES.productImage}
					/>
				</Box>
				<Box sx={ProductDetailsSx.detailsContainer}>
					<ProductNameLink
						href={productHref}
						manufacturer={manufacturer}
						name={name}
						onClick={onTrackSelectItem}
					/>
					<Box display="flex" gap="10px">
						<Typography
							variant="bodyCaption"
							color="grey.300"
							className={FREE_GIFT_CLASSES.productSku}
						>
							{SKU.t()} {product?.partNumber}
						</Typography>
						<ProductQuantityLabel show={!combinable} quantity={quantity} label={Quantity.t()} />
					</Box>
					<ProductPriceSection
						isPurchaseView={isPurchaseView}
						price={price}
						freeLabel={labels.Free.t()}
					/>
					<OutOfStockLabel show={outOfStock} label={Availability.labels.empty.t()} />
					<ProductQuantityInput
						show={combinable && selected && !outOfStock}
						quantity={quantity}
						isParentProduct={isParentProduct}
						maxQuantity={maxQuantity}
						onQuantityChange={onQuantityChange}
					/>
				</Box>
			</Box>
		</Stack>
	);
};
